import type { OBB, Vec2 } from '../../../geo/types';
import type { Role } from '../citizens/citizenActing';

/** The roles of those who keep a place: a seller its pitch, a hand its work. Each such seat is one
 *  resident's own, every time it comes; the others are taken by whoever comes. */
export const KEEPS: ReadonlySet<Role> = new Set<Role>(['sell', 'work']);

/** One place to stand: where, facing which way, doing what, and in whose company. */
export interface Seat {
  readonly at: Vec2;
  /** Radians from east, counter-clockwise, as a figure's heading is. */
  readonly facing: number;
  readonly role: Role;
  /** Seats of one group are in each other's company; numbered from 0 within one laying out. */
  readonly group: number;
}

/** Every measure here is an estimate of how near people stand to each other and to their work. */
export const SEATS = {
  /** A seller stands this far behind the point of its pitch; two buyers this far before the
   *  point and this far to either side, and a third behind them, waiting its turn. */
  pitch: { back: 0.45, before: 0.75, aside: 0.45, behind: 1.5 },
  /** People in talk stand in a ring, of fewer and nearer where there is less room round it. */
  circle: [
    { room: 1.3, seats: 5, radius: 0.8 },
    { room: 0.9, seats: 4, radius: 0.65 },
    { room: 0.5, seats: 3, radius: 0.5 },
    { room: 0, seats: 2, radius: 0.38 },
  ],
  /** Those at work on a thing stand this far off its side, one every so many metres of it. */
  work: { off: 0.7, everyM: 1.8 },
} as const;

const toward = (from: Vec2, to: Vec2): number => Math.atan2(to[1] - from[1], to[0] - from[0]);

/** A seller facing `facing` over the point of its pitch, and three buyers before it, facing it. */
export function pitch(at: Vec2, facing: number): Seat[] {
  const [fx, fy] = [Math.cos(facing), Math.sin(facing)];
  const { back, before, aside, behind } = SEATS.pitch;
  const seller: Vec2 = [at[0] - fx * back, at[1] - fy * back];
  const ahead = (out: number, side: number): Vec2 => [
    at[0] + fx * out - fy * side,
    at[1] + fy * out + fx * side,
  ];
  const buyers = [ahead(before, -aside), ahead(before, aside), ahead(behind, 0)];
  return [
    { at: seller, facing, role: 'sell', group: 0 },
    ...buyers.map((buyer): Seat => ({
      at: buyer,
      facing: toward(buyer, seller),
      role: 'buy',
      group: 0,
    })),
  ];
}

/** People in talk round a point with `room` round it, the first of them at the angle `turn`. */
export function circle(at: Vec2, room: number, turn: number): Seat[] {
  const ring = SEATS.circle.find((size) => room >= size.room)!;
  return Array.from({ length: ring.seats }, (_, k): Seat => {
    const angle = turn + (k * 2 * Math.PI) / ring.seats;
    const p: Vec2 = [at[0] + Math.cos(angle) * ring.radius, at[1] + Math.sin(angle) * ring.radius];
    return { at: p, facing: toward(p, at), role: 'talk', group: 0 };
  });
}

/** Those at work along the two long sides of a box, no more than `most` to a side: each faces
 *  the box and is its own company. */
export function alongside(box: OBB, most = Infinity): Seat[] {
  const long = box.halfU >= box.halfV;
  const [ax, ay] = long ? box.axisU : [-box.axisU[1], box.axisU[0]];
  const [halfLong, halfShort] = long ? [box.halfU, box.halfV] : [box.halfV, box.halfU];
  const count = Math.min(most, Math.max(1, Math.floor((halfLong * 2) / SEATS.work.everyM)));
  const seats: Seat[] = [];
  for (const side of [1, -1])
    for (let k = 0; k < count; k++) {
      const along = ((k + 0.5) / count - 0.5) * 2 * halfLong;
      const off = (halfShort + SEATS.work.off) * side;
      seats.push({
        at: [box.center[0] + ax * along - ay * off, box.center[1] + ay * along + ax * off],
        facing: Math.atan2(-ax * side, ay * side),
        role: 'work',
        group: seats.length,
      });
    }
  return seats;
}
